import NiceModal from "@ebay/nice-modal-react";
import type { ComponentType, FC } from "react";

import { handleChunkLoadError } from "@/core/lib/chunk-reload";

// the extension point. features augment this via `declare module`, type-only,
// so it is erased at runtime: a caller opening a modal by key never imports,
// nor even type-imports, the owning feature. each entry: { payload; result }.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ModalContract {}

export type ModalKey = keyof ModalContract;

type PayloadOf<K extends ModalKey> = ModalContract[K] extends {
  payload: infer P;
}
  ? P
  : never;
type ResultOf<K extends ModalKey> = ModalContract[K] extends { result: infer R }
  ? R
  : void;

// the runtime key -> lazy component map. the app layer fills it once; this
// module is the only cross-cutting thing a feature imports to open a modal.
// the mapped type is exhaustive over `keyof ModalContract`, so a declared modal
// with no loader here (or a loader for an undeclared key) is a compile error.
type ModalLoader = () => Promise<{ default: ComponentType<never> }>;
export type ModalRegistry = Record<ModalKey, ModalLoader>;

const registry = new Map<string, ModalLoader>();

export function registerModals(map: ModalRegistry): void {
  for (const [key, loader] of Object.entries(map)) {
    registry.set(key, loader as ModalLoader);
  }
}

export async function openModal<K extends ModalKey>(
  key: K,
  ...args: PayloadOf<K> extends void ? [] : [payload: PayloadOf<K>]
): Promise<ResultOf<K>> {
  const loader = registry.get(key);
  if (!loader) throw new Error(`no modal registered for key "${key}"`);
  let mod: Awaited<ReturnType<ModalLoader>>;
  try {
    mod = await loader();
  } catch (error) {
    // stale hashed chunk after a deploy: recover with a reload instead of
    // letting the caller's catch render it as a feature-level failure
    if (error instanceof Error) handleChunkLoadError(error, "modal");
    throw error;
  }
  const Component = mod.default as unknown as FC<Record<string, unknown>>;
  return NiceModal.show(
    Component,
    (args[0] ?? {}) as Record<string, unknown>,
  ) as Promise<ResultOf<K>>;
}
